/**
 * How the airship behaves moored and under way (ADR 0033, lifted from
 * brad-airship `airship-flight.js` and `airship-wind.js` `updateWind`).
 * Moored: sails brailed, the mooring gear present, a slow roll against the
 * lines, faint wind wisps. Under way: the jetty falls astern, the sails are
 * set and fill, the ship rolls and pitches more, wind streaks race past the
 * hull and the lift stones brighten. The game shows the ship flying while the
 * flight's banner holds (the wheel's route; slice 7a's flow); `flight` is
 * that state, eased here.
 *
 * - Named parts (`world.ts`) fade as one: the furled and the set sails, the
 *   ensign moored and under way (struck at night), the laundry (out on fair
 *   days only: a time of day with no lamps), and the mooring, which also
 *   falls away astern.
 * - The deck's planking holds a wet sheen at dawn (`dew`).
 * - The lift stones breathe, brighter at night and when carrying the ship;
 *   sailcloth gets a small self-light (translucent canvas against the sun).
 * - The wind streaks (one additive draw of ribbons sliding aft past the hull)
 *   and the galley smoke (a dozen soft billboards leaning downwind) live here.
 * - The attitude (roll, pitch, heave) moves the sky's horizon, not the ship:
 *   the camera rides with the ship.
 */
import * as THREE from 'three';
import type { WindUniforms } from './hooks.ts';
import { flags } from './kit.ts';

/** A named part of the deck: its fade (0 shown, 1 gone) and its meshes. */
export type ShipPart = { readonly fade: THREE.IUniform<number>; readonly meshes: readonly THREE.Mesh[] };

/** What the motion reads each frame from the time of day. */
export type MotionInput = {
  readonly wind: number;
  readonly lamps: number;
  readonly sunColor: THREE.Color;
  readonly sunAtShip: number;
  /** 0 moored, 1 under way (the flow's state). */
  readonly flight: number;
  readonly dew: number;
  readonly smoke: number;
  /** Off, or reduced motion: no easing, the parts take the state at once (the clock does not run). */
  readonly snap: boolean;
};

/** The ship's attitude this frame (radians, metres). */
export type Attitude = { roll: number; pitch: number; heave: number };

export type ShipMotion = {
  readonly streaks: THREE.Mesh;
  readonly smoke: THREE.Mesh;
  readonly attitude: Attitude;
  /** The eased flight (0 moored .. 1 under way). */
  blend(): number;
  /** Advances by `dt`; true when a part's visibility changed (the shadow map needs a redraw). */
  update(dt: number, input: MotionInput): boolean;
  /** Frees the streaks' and the smoke's GPU buffers and programs (Off draws neither; a later draw uploads them again). */
  release(): void;
  dispose(): void;
};

/** A time of day this dark is night (the ensign is struck; the streaks dim; the smoke darkens: the prototype's evening). */
const night = 1;
/** A time of day this dark is dusk (the smoke is dimmed: the prototype's twilight). */
const dusk = 0.6;

const createStreaks = (count = 90): { readonly mesh: THREE.Mesh; update(dt: number, blend: number, input: MotionInput): void; dispose(): void } => {
  const g = new THREE.BufferGeometry();
  const pos: number[] = [];
  const seed: number[] = [];
  const side: number[] = [];
  let s = 7;
  const r = (): number => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
  for (let i = 0; i < count; i += 1) {
    const base = [r(), r(), r(), r()];
    for (const [u, v] of [
      [0, -1],
      [1, -1],
      [1, 1],
      [0, -1],
      [1, 1],
      [0, 1],
    ] as const) {
      pos.push(0, 0, 0);
      seed.push(...base);
      side.push(u, v);
    }
  }
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('seed', new THREE.Float32BufferAttribute(seed, 4));
  g.setAttribute('corner', new THREE.Float32BufferAttribute(side, 2));
  // depthWrite: the lens blurs and tints every pixel still at the far plane (the sky), which erased streaks drawn over it.
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: true,
    blending: THREE.NormalBlending,
    uniforms: { time: { value: 0 }, speed: { value: 3 }, travel: { value: 0 }, opacity: { value: 0.1 }, color: { value: new THREE.Color(1, 1, 1) }, density: { value: 0.2 } },
    vertexShader: `attribute vec4 seed;attribute vec2 corner;uniform float travel,density;varying float vAlpha;varying vec2 vCorner;
      void main(){
        float live=step(seed.w,density);
        float len=3.5+seed.w*9.;
        vec3 p=vec3(0.);
        p.x=66.-mod(seed.x*70.+travel*(.7+seed.y*.6),70.);
        float side=seed.z<.62?1.:-1.;
        p.z=side*(8.9+fract(seed.z*7.31)*6.);
        p.y=-6.5+seed.y*11.;
        vec4 view=viewMatrix*vec4(p+vec3(corner.x*len,0.,0.),1.);
        view.y+=corner.y*(.04+.05*seed.z);
        gl_Position=projectionMatrix*view;
        vAlpha=live*(1.-smoothstep(56.,70.,p.x))*smoothstep(-30.,-18.,p.x);
        vCorner=corner;
      }`,
    fragmentShader: `uniform float opacity;uniform vec3 color;varying float vAlpha;varying vec2 vCorner;
      void main(){float a=vAlpha*opacity*(1.-abs(vCorner.y))*smoothstep(0.,.3,vCorner.x)*(1.-smoothstep(.5,1.,vCorner.x));gl_FragColor=vec4(color,a);}`,
  });
  material.name = 'airship-streaks';
  const mesh = new THREE.Mesh(g, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 5;
  mesh.name = 'Wind streaks';
  const white = new THREE.Color(1, 1, 1);
  const u = material.uniforms;
  return {
    mesh,
    update(dt, blend, input) {
      (u['travel'] as THREE.IUniform<number>).value += dt * (2.2 + 34 * blend);
      (u['density'] as THREE.IUniform<number>).value = 0.1 + 0.8 * blend;
      (u['opacity'] as THREE.IUniform<number>).value = (0.12 + 0.5 * blend) * (input.lamps >= night ? 0.5 : 1);
      (u['color'] as THREE.IUniform<THREE.Color>).value.copy(input.sunColor).lerp(white, 0.5);
    },
    dispose() {
      g.dispose();
      material.dispose();
    },
  };
};

/** Galley smoke: a dozen soft billboards rising from the stovepipe and leaning downwind (aft). */
const createSmoke = (origin: readonly [number, number, number], count = 14): { readonly mesh: THREE.Mesh; update(dt: number, blend: number, input: MotionInput): void; dispose(): void } => {
  const g = new THREE.BufferGeometry();
  const pos: number[] = [];
  const seed: number[] = [];
  const corner: number[] = [];
  for (let i = 0; i < count; i += 1) {
    for (const [u, v] of [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, -1],
      [1, 1],
      [-1, 1],
    ] as const) {
      pos.push(...origin);
      seed.push(i / count, Math.sin(i * 12.9898) * 0.5 + 0.5);
      corner.push(u, v);
    }
  }
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('seed', new THREE.Float32BufferAttribute(seed, 2));
  g.setAttribute('corner', new THREE.Float32BufferAttribute(corner, 2));
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { time: { value: 0 }, amount: { value: 0.5 }, lean: { value: 1 }, light: { value: new THREE.Color(1, 1, 1) }, shade: { value: new THREE.Color(0.4, 0.42, 0.48) } },
    vertexShader: `attribute vec2 seed,corner;uniform float time,lean;varying float vAge,vSeed;varying vec2 vCorner;
      void main(){
        float age=fract(time*.09+seed.x);
        vec3 p=position+vec3(-age*age*5.5*lean,age*3.8,sin(seed.y*6.3+time*.7)*.35*age);
        float size=.3+age*2.3;
        vec4 view=viewMatrix*vec4(p,1.);
        view.xy+=corner*size;
        gl_Position=projectionMatrix*view;
        vAge=age;vSeed=seed.y;vCorner=corner;
      }`,
    fragmentShader: `uniform float amount;uniform vec3 light,shade;varying float vAge,vSeed;varying vec2 vCorner;
      void main(){
        float r=length(vCorner);
        float puff=(1.-smoothstep(.2,1.,r))*(.75+.25*sin(vCorner.x*3.+vSeed*9.));
        float a=puff*amount*smoothstep(0.,.08,vAge)*(1.-vAge)*.62;
        if(a<.01)discard;
        vec3 c=mix(shade,light,clamp(.5-vCorner.y*.4+vAge*.3,0.,1.));
        gl_FragColor=vec4(c,a);
      }`,
  });
  material.name = 'airship-smoke';
  const mesh = new THREE.Mesh(g, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 4;
  mesh.name = 'Galley smoke';
  const u = material.uniforms;
  const cool = new THREE.Color(0.85, 0.85, 0.9);
  return {
    mesh,
    update(dt, blend, input) {
      (u['time'] as THREE.IUniform<number>).value += dt;
      const amount = u['amount'] as THREE.IUniform<number>;
      amount.value += (input.smoke - amount.value) * Math.min(1, dt);
      (u['lean'] as THREE.IUniform<number>).value = 0.6 + input.wind + 2.5 * blend;
      const light = (u['light'] as THREE.IUniform<THREE.Color>).value;
      light.copy(input.sunColor).lerp(cool, 0.4);
      if (input.lamps >= night) light.setRGB(0.12, 0.14, 0.22);
      else if (input.lamps >= dusk) light.multiplyScalar(0.6);
      (u['shade'] as THREE.IUniform<THREE.Color>).value.copy(light).multiplyScalar(0.45);
    },
    dispose() {
      g.dispose();
      material.dispose();
    },
  };
};

const smoothstep = (x: number, a: number, b: number): number => THREE.MathUtils.smoothstep(x, a, b);

/** The deck's motion over its parts and materials (`createShipMotion`). */
export const createShipMotion = (options: {
  readonly root: THREE.Object3D;
  readonly parts: ReadonlyMap<string, ShipPart>;
  readonly wind: WindUniforms;
  readonly lampLevel: THREE.IUniform<number>;
}): ShipMotion => {
  const { parts, wind } = options;
  const liftMaterials = new Set<THREE.MeshStandardMaterial>();
  const canvasMaterials = new Set<THREE.MeshStandardMaterial>();
  let deckMaterial: THREE.MeshStandardMaterial | null = null;
  options.root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const list = (Array.isArray(o.material) ? o.material : [o.material]) as THREE.Material[];
    for (const mat of list) {
      if (!(mat instanceof THREE.MeshStandardMaterial)) continue;
      if (flags(mat).liftGlow !== undefined) liftMaterials.add(mat);
      if (mat.name === 'airship-deck') deckMaterial = mat;
      // Sailcloth is translucent: a sail seen against the sun never goes slate-dark. A small self-light stands in.
      if (/airship-(mainsail|foresail|furl)/.test(mat.name)) canvasMaterials.add(mat);
    }
  });
  for (const m of canvasMaterials) m.emissive.copy(m.color).multiplyScalar(0.9);
  const streaks = createStreaks();
  const smoke = createSmoke([55.4, 2.78, -2.6]);
  let blend = 0;
  let flightLerp = 0;
  let angle = 0;
  let gustPhase = 0;
  let fadeNight = 0;
  let laundryLerp = 0;
  let dew = 0;
  const attitude: Attitude = { roll: 0, pitch: 0, heave: 0 };
  const fadePart = (name: string, fade: number): boolean => {
    const p = parts.get(name);
    if (p === undefined) return false;
    p.fade.value = fade;
    const show = fade < 0.999;
    let changed = false;
    for (const m of p.meshes) {
      if (m.visible !== show) {
        m.visible = show;
        changed = true;
      }
    }
    return changed;
  };
  return {
    streaks: streaks.mesh,
    smoke: smoke.mesh,
    attitude,
    blend: () => blend,
    update(dt, input) {
      // `updateWind`: the wind's clock, gusts, strength (stronger under way), the propellers and the set sails.
      flightLerp = input.snap ? input.flight : flightLerp + (input.flight - flightLerp) * (1 - Math.exp(-dt * 0.9));
      wind.windTime.value += dt;
      gustPhase += dt * 0.23;
      wind.windGust.value = Math.max(0, Math.sin(gustPhase) * Math.sin(gustPhase * 2.7 + 1.3));
      wind.windStrength.value = input.wind * (1 + 0.6 * flightLerp);
      angle += dt * (0.35 + 22 * flightLerp);
      wind.propAngle.value = angle % (Math.PI * 2000);
      wind.sailSet.value = flightLerp;
      blend = flightLerp;
      // Sails: the brails come off first, then the canvas is hoisted and fills.
      let changed = fadePart('sailFurled', smoothstep(blend, 0.05, 0.45));
      changed = fadePart('sailSet', 1 - smoothstep(blend, 0.35, 0.8)) || changed;
      // The ensign: struck at night; on the stern staff moored, at the gaff peak under way.
      const struck = input.lamps >= night ? 1 : 0;
      fadeNight = input.snap ? struck : fadeNight + (struck - fadeNight) * Math.min(1, dt * 1.5);
      changed = fadePart('ensignMoored', Math.max(fadeNight, smoothstep(blend, 0.05, 0.45))) || changed;
      changed = fadePart('ensignFlying', Math.max(fadeNight, 1 - smoothstep(blend, 0.35, 0.8))) || changed;
      // Laundry is out on fair days (no lamps lit) and in at dusk and before casting off.
      const laundryOut = input.lamps === 0 && blend < 0.2 ? 1 : 0;
      laundryLerp = input.snap ? laundryOut : laundryLerp + (laundryOut - laundryLerp) * Math.min(1, dt * 1.5);
      changed = fadePart('laundry', 1 - laundryLerp) || changed;
      // Dawn dew: the planking holds a wet sheen until the sun burns it off.
      const deckMat: THREE.MeshStandardMaterial | null = deckMaterial;
      if (deckMat !== null) {
        dew = input.snap ? input.dew : dew + (input.dew - dew) * Math.min(1, dt * 0.8);
        deckMat.roughness = 0.92 - 0.42 * dew;
      }
      changed = fadePart('mooring', smoothstep(blend, 0.1, 0.7)) || changed;
      for (const m of parts.get('mooring')?.meshes ?? []) {
        m.position.x = -Math.pow(blend, 1.6) * 40;
        m.position.y = -Math.pow(blend, 2) * 6;
      }
      // The lift stones breathe, brighter at night and when carrying the ship.
      const t = wind.windTime.value;
      for (const m of liftMaterials) m.emissiveIntensity = (flags(m).liftGlow ?? 0) * (0.55 + 1.4 * input.lamps + 0.8 * blend) * (0.88 + 0.12 * Math.sin(t * 1.3));
      options.lampLevel.value = input.lamps;
      const back = 0.2 * (1 - 0.8 * input.lamps) * input.sunAtShip;
      for (const m of canvasMaterials) m.emissiveIntensity = back;
      streaks.update(dt, blend, input);
      smoke.update(dt, blend, input);
      // Roll, pitch and heave: the camera rides with the ship, so only the world outside moves (the sky's horizon).
      const amp = 0.004 + 0.013 * blend;
      const gust = wind.windGust.value;
      attitude.roll = amp * (Math.sin(t * 0.55) + 0.4 * Math.sin(t * 1.37 + 1)) * (1 + gust);
      attitude.pitch = amp * 0.55 * Math.sin(t * 0.43 + 2);
      attitude.heave = (0.05 + 0.3 * blend) * Math.sin(t * 0.61);
      return changed;
    },
    release() {
      streaks.dispose();
      smoke.dispose();
    },
    dispose() {
      streaks.dispose();
      smoke.dispose();
    },
  };
};
